import { useMemo } from 'react';
import { useBracketStore } from '@/stores/bracketStore';
import { allTournamentGames, type TournamentGame } from '@/data/tournament';

/**
 * Build a map of gameId -> feeder games (games whose nextGameId points here).
 * Feeders are sorted by id for deterministic slot assignment.
 */
const feederMap = new Map<string, TournamentGame[]>();

const roundRank: Record<string, number> = {
  first_four: 0,
  first_round: 1,
  second_round: 2,
  sweet_sixteen: 3,
  elite_eight: 4,
  final_four: 5,
  championship: 6,
};

const getFeederSlotByNumericOrder = (targetGameId: string, feederGameId: string): 'team1' | 'team2' | null => {
  const target = parseGameIndex(targetGameId);
  const feeder = parseGameIndex(feederGameId);

  // Known deterministic rule for standard bracket structure:
  // odd feeder game number fills the top slot (team1), even fills the bottom slot (team2).
  // This applies to normal 2-feeder transitions, including R2 -> S16.
  if (target.roundGroup !== Number.MAX_SAFE_INTEGER && feeder.roundGroup !== Number.MAX_SAFE_INTEGER) {
    const expectedNextRound = target.roundGroup - 1;
    if (feeder.roundGroup === expectedNextRound && feeder.index >= 1) {
      return feeder.index % 2 === 1 ? 'team1' : 'team2';
    }
  }

  return null;
};

const parseGameIndex = (id: string) => {
  const m = id.match(/game-(\d+)-(\d+)/);
  if (!m) return { roundGroup: Number.MAX_SAFE_INTEGER, index: Number.MAX_SAFE_INTEGER };
  return {
    roundGroup: Number(m[1]),
    index: Number(m[2]),
  };
};

const resolveCanonicalSourceSlot = (targetGameId: string, feederGameId: string): 'team1' | 'team2' | null => {
  const target = parseGameIndex(targetGameId);
  const feeder = parseGameIndex(feederGameId);

  if (target.roundGroup === Number.MAX_SAFE_INTEGER || feeder.roundGroup === Number.MAX_SAFE_INTEGER) {
    return getFeederSlotByNumericOrder(targetGameId, feederGameId);
  }

  // Explicit bracket rule: RkN game i feeds slot by pair order into R(k+1) game ceil(i/2):
  // odd-numbered source -> team1 slot, even-numbered source -> team2 slot.
  if (feeder.roundGroup + 1 !== target.roundGroup) {
    return getFeederSlotByNumericOrder(targetGameId, feederGameId);
  }

  const expectedTop = `${target.roundGroup - 1}-${(target.index * 2) - 1}`;
  const expectedBottom = `${target.roundGroup - 1}-${target.index * 2}`;
  const feederSuffix = `${feeder.roundGroup}-${feeder.index}`;

  if (feederSuffix === expectedTop) return 'team1';
  if (feederSuffix === expectedBottom) return 'team2';

  return getFeederSlotByNumericOrder(targetGameId, feederGameId);
};


for (const g of allTournamentGames) {
  if (g.nextGameId) {
    const arr = feederMap.get(g.nextGameId) || [];
    arr.push(g);
    feederMap.set(g.nextGameId, arr);
  }
}
for (const [, arr] of feederMap) {
  arr.sort((a, b) => {
    const rankA = roundRank[a.round] || 0;
    const rankB = roundRank[b.round] || 0;
    if (rankA !== rankB) return rankA - rankB;

    const aIdx = parseGameIndex(a.id);
    const bIdx = parseGameIndex(b.id);

    if (aIdx.roundGroup !== bIdx.roundGroup) {
      return aIdx.roundGroup - bIdx.roundGroup;
    }
    return aIdx.index - bIdx.index;
  });
}

/**
 * Recursively project a game's team slots based on user picks.
 *
 * Two cases:
 * 1) Play-in → R1: game already has one real team + one 'tbd'. Single feeder
 *    fills the tbd slot only.
 * 2) Normal progression (R1→R2, R2→S16, etc.): two feeders, each provides
 *    a team slot. Feeder[0] → team1, feeder[1] → team2.
 */
export function projectGame(
  game: TournamentGame,
  picks: Record<string, { teamId: string }>,
): TournamentGame {
  const feeders = feederMap.get(game.id);

  if (!feeders || feeders.length === 0) {
    // Leaf game — no feeders, use static data as-is
    return game;
  }

  // Check if this is a play-in → R1 scenario:
  // Game already has one real team and one 'tbd', with exactly 1 feeder
  const isPlayInTarget =
    feeders.length === 1 &&
    feeders[0].isPlayIn &&
    ((game.team1Id && game.team1Id !== 'tbd') || (game.team2Id && game.team2Id !== 'tbd'));

  if (isPlayInTarget) {
    // Play-in feeder fills the 'tbd' slot
    const feeder = feeders[0];
    const projectedFeeder = projectGame(feeder, picks);
    const pick = picks[projectedFeeder.id];

    let { team1Id, team1Seed, team2Id, team2Seed } = game;

    if (pick) {
      // Figure out which slot is tbd and fill it
      if (team1Id === 'tbd' || !team1Id) {
        team1Id = pick.teamId;
        team1Seed = pick.teamId === projectedFeeder.team1Id
          ? projectedFeeder.team1Seed
          : projectedFeeder.team2Seed;
      } else if (team2Id === 'tbd' || !team2Id) {
        team2Id = pick.teamId;
        team2Seed = pick.teamId === projectedFeeder.team1Id
          ? projectedFeeder.team1Seed
          : projectedFeeder.team2Seed;
      }
    }

    return { ...game, team1Id, team1Seed, team2Id, team2Seed };
  }

  // Normal case: 2 feeders → deterministic top/bottom slot mapping by feeder game number parity.
  const projectedFeeders = feeders.map(f => projectGame(f, picks));
  const orderedFeeders = [...projectedFeeders].sort((a, b) => {
    const aIdx = parseGameIndex(a.id);
    const bIdx = parseGameIndex(b.id);
    if (aIdx.roundGroup !== bIdx.roundGroup) return aIdx.roundGroup - bIdx.roundGroup;
    return aIdx.index - bIdx.index;
  });

  let team1Id = game.team1Id;
  let team1Seed = game.team1Seed;
  let team2Id = game.team2Id;
  let team2Seed = game.team2Seed;

  const assignSlot = (feederGame: TournamentGame, targetSlot: 'team1' | 'team2') => {
    const pick = picks[feederGame.id];
    if (!pick) return;

    if (targetSlot === 'team1') {
      team1Id = pick.teamId;
      if (pick.teamId === feederGame.team1Id) {
        team1Seed = feederGame.team1Seed;
      } else if (pick.teamId === feederGame.team2Id) {
        team1Seed = feederGame.team2Seed;
      }
      return;
    }

    team2Id = pick.teamId;
    if (pick.teamId === feederGame.team1Id) {
      team2Seed = feederGame.team1Seed;
    } else if (pick.teamId === feederGame.team2Id) {
      team2Seed = feederGame.team2Seed;
    }
  };

  if (orderedFeeders[0]) {
    const slot = resolveCanonicalSourceSlot(game.id, orderedFeeders[0].id);
    if (slot) {
      assignSlot(orderedFeeders[0], slot);
    } else {
      // Fallback to existing behavior if parsing unexpected
      assignSlot(orderedFeeders[0], 'team1');
    }
  }

  if (orderedFeeders[1]) {
    const slot = resolveCanonicalSourceSlot(game.id, orderedFeeders[1].id);
    if (slot) {
      assignSlot(orderedFeeders[1], slot);
    } else {
      assignSlot(orderedFeeders[1], 'team2');
    }
  }

  return { ...game, team1Id, team1Seed, team2Id, team2Seed };
}

/**
 * React hook: projects team IDs for a game based on current picks.
 * Reactive via Zustand picks selector.
 */
export function useProjectedGame(game: TournamentGame): TournamentGame {
  const picks = useBracketStore(s => s.picks);
  return useMemo(() => projectGame(game, picks), [game.id, picks]);
}
